// frontend/src/pages/sales/fulfilment/DispatchCommitmentStrip.tsx
/**
 * Phase 2 plan 12 — dispatch-date commitment on the order (10-Aug §3.1 step 5:
 * "committed date visible to the sales team; any change notifies sales").
 * Anyone can read it; the server decides who may set it (dispatch.form / production.release).
 */
import { useEffect, useState } from 'react';
import { CalendarClock } from 'lucide-react';
import { api } from '@/utils/api';

interface CommitmentView { id: string; salesOrderId: string; status: 'awaiting' | 'committed' | 'dispatched' | 'cancelled'; openedAt: string; dueBy: string; committedDate: string | null; committedByName: string | null; changeCount: number; overdueForCommit: boolean; changes: Array<{ fromDate: string | null; toDate: string; reason: string | null; changedByName: string; at: string }> }

const OVERRIDE_OPTIONS: Array<[string, string]> = [
  ['CAPACITY_CHANGED', 'Capacity on the floor changed'], ['MATERIAL_DATE', 'Material arrives on a different date'],
  ['CUSTOMER_DATE', 'Customer asked for this date'], ['PRIORITY', 'Another order was put first'], ['OTHER', 'Other (write why)'],
];

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const ymdLabel = (ymd: string) => `${Number(ymd.slice(8, 10))} ${MONTHS[Number(ymd.slice(5, 7)) - 1]}`;
function istLabel(iso: string) {
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kolkata', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(new Date(iso)).map((x) => [x.type, x.value]));
  return `${ymdLabel(`${p.year}-${p.month}-${p.day}`)}, ${p.hour}:${p.minute}`;
}
const times = (n: number) => (n === 1 ? 'once' : `${n} times`);

export function DispatchCommitmentStrip({ salesOrderId }: { salesOrderId: string }) {
  const [view, setView] = useState<CommitmentView | null | undefined>(undefined);
  const [editing, setEditing] = useState(false);
  const [date, setDate] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<{ message: string; code?: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [suggestion, setSuggestion] = useState<{ suggestedDate: string | null; label: string } | null>(null);
  const [overrideCode, setOverrideCode] = useState('');
  const [overrideNote, setOverrideNote] = useState('');

  useEffect(() => {
    let active = true;
    setView(undefined);
    setSuggestion(null); setOverrideCode(''); setOverrideNote('');
    setEditing(false);
    setDate('');
    setReason('');
    setError(null);
    setSaving(false);
    void api.get(`/dispatch-commitments/sales-order/${salesOrderId}`)
      .then((res) => { if (active) setView(res.data.data); })
      .catch(() => { if (active) setView(null); });
    return () => { active = false; };
  }, [salesOrderId]);

  if (!view) return null;

  const last = view.changes.length ? view.changes[view.changes.length - 1] : null;
  const text = view.status === 'awaiting'
    ? (view.overdueForCommit ? `No dispatch date yet — was due by ${istLabel(view.dueBy)}. Owners and Pawan have been told.` : `No dispatch date yet — due by ${istLabel(view.dueBy)}.`)
    : view.status === 'cancelled' ? 'Dispatch commitment cancelled.'
    : view.status === 'dispatched' ? `Dispatched against committed date ${view.committedDate ? ymdLabel(view.committedDate) : '—'}.`
    : `Dispatch date ${(view.committedDate ? ymdLabel(view.committedDate) : '—')} · committed by ${view.committedByName ?? '—'}${view.changeCount ? ` · changed ${times(view.changeCount)} (last: ${last?.reason ?? '—'})` : ''}`;

  const openEditor = async () => {
    setEditing(true); setDate(view.committedDate ?? '');
    setSuggestion(null); setOverrideCode(''); setOverrideNote(''); setError(null);
    try {
      const r = await api.get(`/dispatch-commitments/sales-order/${salesOrderId}/suggestion`);
      const d = r?.data?.data;
      setSuggestion(d && typeof d.label === 'string' ? { suggestedDate: d.suggestedDate ?? null, label: d.label } : null);
    } catch { setSuggestion(null); }
  };
  const differs = !!(suggestion?.suggestedDate && date && date !== suggestion.suggestedDate);

  const save = async () => {
    setSaving(true); setError(null);
    try {
      const body: Record<string, string> = { date };
      if (reason.trim()) body.reason = reason.trim();
      if (differs && overrideCode) { body.overrideReasonCode = overrideCode; if (overrideCode === 'OTHER' && overrideNote.trim()) body.overrideNote = overrideNote.trim(); }
      const res = await api.post(`/dispatch-commitments/sales-order/${salesOrderId}`, body);
      setView(res.data.data); setEditing(false); setReason('');
    } catch (err: any) {
      setError({ message: err?.response?.data?.error || 'Could not save the dispatch date.', code: err?.response?.data?.code });
    } finally { setSaving(false); }
  };

  const canEdit = view.status === 'awaiting' || view.status === 'committed';
  return (
    <div data-testid="commitment-strip" className="mb-6 rounded-xl border p-4" style={{ background: 'var(--bg-surface)', borderColor: view.overdueForCommit ? 'var(--danger)' : 'var(--border-primary)' }}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p data-testid="commitment-text" className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-primary)' }}>
          <CalendarClock size={14} style={{ color: 'var(--text-muted)' }} />{text}
        </p>
        {canEdit && !editing && (
          <button onClick={openEditor} className="rounded-lg px-3 py-1.5 text-sm font-medium" style={{ background: 'var(--bg-muted)', color: 'var(--text-secondary)' }}>
            {view.committedDate ? 'Change date' : 'Set dispatch date'}
          </button>
        )}
      </div>
      {editing && (
        <div className="mt-3 flex flex-wrap items-end gap-3">
          {suggestion && (
            <p data-testid="commitment-suggestion" className="w-full text-xs" style={{ color: 'var(--text-muted)' }}>
              {suggestion.label}
              {suggestion.suggestedDate && (
                <button type="button" onClick={() => setDate(suggestion.suggestedDate as string)} className="ml-2 rounded-lg px-2 py-0.5 text-xs font-medium" style={{ background: 'var(--bg-muted)', color: 'var(--text-secondary)' }}>Use suggested date</button>
              )}
            </p>
          )}

          <label className="text-xs" style={{ color: 'var(--text-muted)' }}>Dispatch date
            <input aria-label="Dispatch date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1 block rounded-lg border px-3 py-1.5 text-sm" style={{ borderColor: 'var(--border-primary)' }} />
          </label>
          {differs && (
            <label className="text-xs" style={{ color: 'var(--text-muted)' }}>Why a different date
              <select aria-label="Why a different date" value={overrideCode} onChange={(e) => setOverrideCode(e.target.value)} className="mt-1 block rounded-lg border px-3 py-1.5 text-sm" style={{ borderColor: 'var(--border-primary)' }}>
                <option value="">Pick a reason</option>
                {OVERRIDE_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
          )}
          {differs && overrideCode === 'OTHER' && (
            <label className="flex-1 text-xs" style={{ color: 'var(--text-muted)' }}>Override note
              <input aria-label="Override note" value={overrideNote} onChange={(e) => setOverrideNote(e.target.value)} className="mt-1 block w-full rounded-lg border px-3 py-1.5 text-sm" style={{ borderColor: 'var(--border-primary)' }} />
            </label>
          )}

          {view.committedDate && (
            <label className="flex-1 text-xs" style={{ color: 'var(--text-muted)' }}>Reason for change
              <input aria-label="Reason for change" value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 block w-full rounded-lg border px-3 py-1.5 text-sm" style={{ borderColor: 'var(--border-primary)' }} />
            </label>
          )}
          <button onClick={save} disabled={saving || !date} className="rounded-lg px-3 py-1.5 text-sm font-medium disabled:opacity-50" style={{ background: 'var(--bg-muted)', color: 'var(--text-primary)' }}>Save date</button>
        </div>
      )}
      {error && <p role="alert" data-error-code={error.code} className="mt-2 text-sm" style={{ color: 'var(--danger)' }}>{error.message}</p>}
    </div>
  );
}
